// Locale-aware formatting for event data.
// Shared verbatim with mobile/utils/format.js.

import { INTL_LOCALE, translate } from '../i18n/dictionary';

// Event dates are naive wall-clock keys ("2026-09-12 20:00"). Parsing them by
// hand keeps them wall-clock in every timezone and avoids Safari's refusal to
// parse the space-separated form.
export function parseEventDate(value) {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const match = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/
  );

  if (match) {
    const [, year, month, day, hour = '0', minute = '0'] = match;
    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute)
    );
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

export function toDayKey(date) {
  if (!date) {
    return '';
  }
  const pad = (value) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function intl(locale) {
  return INTL_LOCALE[locale] || INTL_LOCALE.hu;
}

// "SZE 12. SZEPT" / "WED 12 SEP" - the compact form used on cards.
export function formatCardDate(value, locale) {
  const date = parseEventDate(value);
  if (!date) {
    return '';
  }

  const weekday = date
    .toLocaleDateString(intl(locale), { weekday: 'short' })
    .replace('.', '')
    .toUpperCase();
  const day = date.toLocaleDateString(intl(locale), {
    day: 'numeric',
    month: 'short'
  });

  return `${weekday} ${day.toUpperCase()}`;
}

export function formatTime(value, locale) {
  const date = parseEventDate(value);
  if (!date) {
    return '';
  }
  return date.toLocaleTimeString(intl(locale), {
    hour: '2-digit',
    minute: '2-digit'
  });
}

export function formatFullDate(value, locale) {
  const date = parseEventDate(value);
  if (!date) {
    return '';
  }
  return date.toLocaleDateString(intl(locale), {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Heading for a day group in the results list.
export function formatDayHeading(value, locale) {
  const date = parseEventDate(value);
  if (!date) {
    return '';
  }
  return date.toLocaleDateString(intl(locale), {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  });
}

export function formatAmount(amount, locale) {
  return `${new Intl.NumberFormat(intl(locale)).format(amount)} Ft`;
}

export function formatPrice(lowestPrice, locale, t) {
  if (lowestPrice === 0) {
    return t('price.free');
  }
  if (lowestPrice === null || lowestPrice === undefined) {
    return t('price.unknown');
  }
  return t('price.from', { amount: formatAmount(lowestPrice, locale) });
}

export function formatDistance(distanceKm, t) {
  if (distanceKm === null || distanceKm === undefined) {
    return null;
  }
  return t('distance.away', {
    km: distanceKm < 10 ? distanceKm.toFixed(1) : Math.round(distanceKm)
  });
}

export function formatDistrict(district, locale, t) {
  if (!district) {
    return null;
  }
  return t('district.label', { district });
}

const TICKET_STATUS = {
  'sold out': 'soldout',
  soldout: 'soldout',
  limited: 'limited',
  available: 'available'
};

export function getTicketStatus(ticketsAvailable) {
  return TICKET_STATUS[String(ticketsAvailable || '').trim().toLowerCase()] || null;
}

export function makeTranslator(locale) {
  return (key, values) => translate(locale, key, values);
}
